import type { IConversationMessage } from '../../hooks/useAssistantWebSocket';

/**
 * The alert each assistant reply was opened on, keyed by message id. Empty for
 * a transcript holding no alert cards.
 *
 * An `alert-card` row claims the next assistant message: the server emits the
 * card ahead of the reply on a live expand, and `snapshotTranscript` projects
 * the same order from the stored instruction row, so one rule covers both the
 * live and the reloaded transcript. A card whose turn is still streaming, or
 * whose turn errored before writing a reply, pairs with nothing and is absent
 * from the map.
 *
 * Tool-call lines do not break the pairing — they are turn activity, not the
 * reply — but any other row does: a user bubble is the next question, and a
 * second assistant message belongs to a later round.
 */
export function alertIdByMessageId(messages: readonly IConversationMessage[]): Map<string, string> {
  const byMessageId = new Map<string, string>();
  let openAlertId: string | null = null;

  for (const message of messages) {
    if (message.kind === 'alert-card') {
      openAlertId = message.alertId ?? null;
      continue;
    }

    if (message.kind === 'tool-call' || openAlertId === null) {
      continue;
    }

    if (message.role === 'assistant') {
      byMessageId.set(message.id, openAlertId);
    }
    openAlertId = null;
  }

  return byMessageId;
}

/** {@link splitRecapLead}'s two halves. */
export interface IRecapSplit {
  /** The opening paragraph — what the assistant did about this alert before now. */
  recap: string;
  /** Everything after it, empty when the summary is a single paragraph. */
  rest: string;
}

/** A blank line, which is what separates markdown blocks. */
const PARAGRAPH_BREAK = /\r?\n[ \t]*\r?\n/;

/**
 * Split a replayed alert summary into its recap and the rest.
 *
 * **The content contract**: the summary's opening paragraph recaps the work
 * already done, and the closing ask plus its `<suggestion="…" />` tokens are
 * later blocks — which is how the channel already teaches the assistant to
 * write ("the suggestion carries an answer, it does not replace the ask"). Only
 * the recap takes the bubble treatment; the ask has to stay ordinary prose or
 * the chips would read as part of a past conversation.
 *
 * A recap that runs to two paragraphs leaves its second one outside the bubble
 * as ordinary prose. That degrades rather than breaks, which is the reason for
 * splitting on the block boundary rather than guessing at sentences.
 */
export function splitRecapLead(content: string): IRecapSplit {
  const match = PARAGRAPH_BREAK.exec(content);

  if (match === null) {
    return { recap: content.trim(), rest: '' };
  }

  return {
    recap: content.slice(0, match.index).trim(),
    rest: content.slice(match.index + match[0].length).trim(),
  };
}
